import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Modal, Button, Slider, Upload, message } from 'antd';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';
import * as utils from '../utils/utils.js';

/**
 * Reusable Image Upload and Cropper Component
 *
 * @param {string} value - Current image URL (from server)
 * @param {function} onChange - Callback when image changes, receives image URL string
 * @param {number} aspectRatio - Aspect ratio for cropping (default: 1)
 * @param {string} cropShape - 'rect' or 'round' (default: 'rect')
 */
export default function ImageUploadCropper({
    value,
    onChange,
    aspectRatio = 1,
    cropShape = 'rect'
}) {
    const [imageSrc, setImageSrc] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [isCropModalVisible, setIsCropModalVisible] = useState(false);
    const [uploading, setUploading] = useState(false);

    // Handle file selection
    const onFileChange = (file) => {
        const reader = new FileReader();
        reader.addEventListener('load', () => {
            setImageSrc(reader.result);
            setIsCropModalVisible(true);
        });
        reader.readAsDataURL(file);
        return false; // Prevent auto upload
    };

    // Handle crop complete
    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    // Create cropped image and upload to server
    const createCroppedImage = async () => {
        try {
            setUploading(true);

            // Get the cropped image as a blob
            const croppedBlob = await getCroppedImg(
                imageSrc,
                croppedAreaPixels
            );

            // Create FormData and append the blob
            const formData = new FormData();
            formData.append('image', croppedBlob, 'cropped-image.jpg');

            // Upload to server
            const token = localStorage.getItem('token');
            const apiUrl = utils.backendUrl();
            const response = await axios.post(
                `${apiUrl}/upload-image`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (response.data.success) {
                // Return the server URL
                const imageUrl = `${apiUrl}${response.data.imageUrl}`;
                onChange(imageUrl);
                message.success('Image uploaded successfully!');
            } else {
                message.error('Failed to upload image');
            }

            setIsCropModalVisible(false);
            setImageSrc(null);
            setUploading(false);
        } catch (e) {
            console.error('Error uploading image:', e);
            message.error('Error uploading image');
            setUploading(false);
        }
    };

    // Remove image
    const removeImage = () => {
        onChange(null);
    };

    return (
        <div style={{ width: '100%' }}>
            {value ? (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img
                        src={value}
                        alt="Preview"
                        style={{
                            width: '150px',
                            height: '150px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            border: '1px solid #d9d9d9'
                        }}
                    />
                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={removeImage}
                        style={{
                            position: 'absolute',
                            top: '5px',
                            right: '5px',
                            background: 'rgba(255, 255, 255, 0.9)'
                        }}
                    />
                </div>
            ) : (
                <Upload
                    accept="image/*"
                    beforeUpload={onFileChange}
                    showUploadList={false}
                >
                    <Button icon={<UploadOutlined />}>
                        Upload Image
                    </Button>
                </Upload>
            )}

            <Modal
                title="Crop Image"
                open={isCropModalVisible}
                onOk={createCroppedImage}
                onCancel={() => {
                    setIsCropModalVisible(false);
                    setImageSrc(null);
                }}
                width={700}
                okText="Save"
                cancelText="Cancel"
                confirmLoading={uploading}
            >
                <div style={{ position: 'relative', width: '100%', height: '400px', background: '#333' }}>
                    {imageSrc && (
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={aspectRatio}
                            cropShape={cropShape}
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                        />
                    )}
                </div>
                <div style={{ marginTop: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px' }}>Zoom</label>
                    <Slider
                        value={zoom}
                        min={1}
                        max={3}
                        step={0.1}
                        onChange={setZoom}
                    />
                </div>
            </Modal>
        </div>
    );
}

// Helper function to create cropped image
const getCroppedImg = (imageSrc, pixelCrop) => {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.src = imageSrc;
        image.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            // Resize to max width of 1200px while maintaining aspect ratio
            const maxWidth = 1200;
            let targetWidth = pixelCrop.width;
            let targetHeight = pixelCrop.height;

            if (targetWidth > maxWidth) {
                const ratio = maxWidth / targetWidth;
                targetWidth = maxWidth;
                targetHeight = targetHeight * ratio;
            }

            canvas.width = targetWidth;
            canvas.height = targetHeight;

            ctx.drawImage(
                image,
                pixelCrop.x,
                pixelCrop.y,
                pixelCrop.width,
                pixelCrop.height,
                0,
                0,
                targetWidth,
                targetHeight
            );

            canvas.toBlob((blob) => {
                if (!blob) {
                    reject(new Error('Canvas is empty'));
                    return;
                }
                // Return the blob directly instead of base64
                resolve(blob);
            }, 'image/jpeg', 0.85);
        };
        image.onerror = reject;
    });
};

